import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { viewAttachment } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { MarkdownLite } from "@/components/help/helpShared";
import { Sparkles, BookOpen, FileText, ThumbsUp, ThumbsDown, LifeBuoy, Loader2, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { article: "View Article", faq: "View FAQ", knowledge: "View Document", certified: "View Source", training: "Open Training", content: "Open" };

function RefRow({ r, i, onOpen }) {
  return (
    <button onClick={() => onOpen(r)} data-testid={`ask-ref-${i}`}
      className="w-full text-left flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:border-brand/40 hover:bg-brand/5 transition-colors">
      {r.kind === "knowledge" || r.kind === "certified" ? <FileText className="w-4 h-4 text-brand mt-0.5 shrink-0" /> : <BookOpen className="w-4 h-4 text-brand mt-0.5 shrink-0" />}
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] uppercase tracking-wide text-slate-400">{r.kind === "training" ? "Related Training" : r.authority}</span>
        <span className="block text-sm font-medium text-slate-800">{r.title}{r.section && <span className="text-slate-500 font-normal"> → {r.section}</span>}</span>
        {r.topics?.length > 0 && <span className="mt-1 flex flex-wrap gap-1" data-testid={`ask-ref-topics-${i}`}>{r.topics.map((t) => <span key={t} className="text-[11px] rounded-full bg-sky-50 text-[#006090] px-1.5">{t}</span>)}</span>}
      </span>
      <span className="text-xs text-brand font-medium shrink-0">{KIND_LABEL[r.kind] || "Open"}</span>
    </button>
  );
}

export function AskTopRecruit({ question, page, onOpenArticle, onTicket, onAsk, onShowAll }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [voted, setVoted] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!question) return;
    setRes(null); setErr(""); setVoted(null);
    api.post("/help/ask", { question, page }).then((r) => setRes(r.data)).catch((e) => setErr(apiError(e)));
  }, [question, page]);
  if (!question) return null;
  const open = async (r) => {
    if (r.kind === "article" || r.kind === "faq") return onOpenArticle(r.slug);
    if (r.open_path && (r.kind === "training" || r.kind === "content")) return navigate(r.open_path);
    try { await viewAttachment(`/knowledge-assistant/sources/${r.source_id}/file`, r.title); } catch (e) { toast.error("This source can't be opened"); }
  };
  const vote = (helpful) => { setVoted(helpful); api.post(`/help/ask/${res.id}/feedback`, { helpful }).catch(() => {}); };
  const ticket = () => { if (res?.id) api.post(`/help/ask/${res.id}/feedback`, { ticket: true }).catch(() => {}); onTicket({ subject: res?.support?.subject || `Help with: ${question.slice(0, 80)}`, description: `Question: ${question}${page ? `\nPage: ${page}` : ""}${res?.references?.length ? `\nArticles checked: ${res.references.map((r) => r.title).join(", ")}` : ""}` }); };
  const answered = res && (res.outcome === "strong" || res.outcome === "weak_answer");
  return (
    <div className="rounded-2xl border border-brand/20 bg-white shadow-sm p-6 mb-6" data-testid="ask-result">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-brand flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />Ask Top-Recruit</p>
      <p className="mt-1 text-slate-500 text-sm" data-testid="ask-question">“{question}”</p>
      {err ? <p className="mt-4 text-sm text-rose-600" data-testid="ask-error">{err}</p> : !res ? (
        <p className="mt-4 text-sm text-slate-500 flex items-center gap-2" data-testid="ask-loading"><Loader2 className="w-4 h-4 animate-spin" />Checking Top-Recruit documentation…</p>
      ) : (
        <>
          <div className={`mt-4 text-[15px] leading-relaxed ${answered ? "text-slate-800" : "text-slate-600"}`} data-testid="ask-answer">
            <MarkdownLite text={res.answer.replace(/\s?\[(\d+)\]/g, "")} />
          </div>
          {res.references?.length > 0 && (
            <div className="mt-5" data-testid="ask-sources">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Learn more · Sources</p>
              <div className="space-y-2">{res.references.map((r, i) => <RefRow key={i} r={r} i={i} onOpen={open} />)}</div>
            </div>
          )}
          {res.related?.length > 0 && (
            <div className="mt-5" data-testid="ask-related">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{answered ? "Related guidance" : "Related information"}</p>
              <div className="flex flex-wrap gap-2">{res.related.map((r, i) => (
                <button key={i} onClick={() => open(r)} data-testid={`ask-related-${i}`} className="text-xs rounded-full border border-slate-200 px-3 py-1 text-slate-600 hover:border-brand/40 hover:text-brand">{r.title}</button>))}</div>
            </div>
          )}
          {answered ? (
            <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="ask-feedback">
                {voted === null ? <>Was this helpful?
                  <button onClick={() => vote(true)} data-testid="ask-helpful-yes" className="p-1.5 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600"><ThumbsUp className="w-4 h-4" /></button>
                  <button onClick={() => vote(false)} data-testid="ask-helpful-no" className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600"><ThumbsDown className="w-4 h-4" /></button></>
                  : voted ? <span className="text-emerald-600">Thanks for the feedback.</span>
                  : <span>Sorry about that. <button onClick={onShowAll} className="text-brand underline" data-testid="ask-search-more">Search more</button> or <button onClick={ticket} className="text-brand underline" data-testid="ask-feedback-ticket">create a support ticket</button>.</span>}
              </div>
              <button onClick={ticket} className="text-xs text-slate-500 hover:text-brand underline-offset-2 hover:underline" data-testid="ask-still-need-help">Still need help? Create Support Ticket</button>
            </div>
          ) : (
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="ask-need-help">
              <p className="font-semibold text-slate-800 text-sm">Need more help?</p>
              <p className="text-sm text-slate-500 mt-0.5">Submit a support ticket and our team can review this with you. Go to <b>{res.support?.path}</b>.</p>
              <Button onClick={ticket} className="mt-3 bg-brand hover:bg-brand-strong text-white" data-testid="ask-create-ticket"><LifeBuoy className="w-4 h-4 mr-1.5" />Create Support Ticket</Button>
            </div>
          )}
          <button onClick={onShowAll} className="mt-4 text-xs text-slate-500 hover:text-brand underline-offset-2 hover:underline" data-testid="ask-view-all">View all search results</button>
        </>
      )}
    </div>
  );
}

export function PopularQuestions({ onAsk }) {
  const [qs, setQs] = useState([]);
  useEffect(() => { api.get("/help/ask/suggestions").then((r) => setQs(r.data.questions || [])).catch(() => {}); }, []);
  if (!qs.length) return null;
  return (
    <div className="mb-8" data-testid="help-popular-questions">
      <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><MessageCircleQuestion className="w-4 h-4 text-slate-400" />Popular questions</h2>
      <div className="flex flex-wrap gap-2">{qs.map((q, i) => (
        <button key={q} onClick={() => onAsk(q)} data-testid={`popular-q-${i}`} className="text-sm rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-slate-700 hover:border-brand/40 hover:text-brand transition-colors">{q}</button>))}</div>
    </div>
  );
}
